import React, { useRef, useEffect, useCallback, useState } from "react";
import { Button, Tooltip } from "@mantine/core";
import TextareaAutosize from "react-textarea-autosize";
import { Refresh, Copy } from "tabler-icons-react";
import { useChat } from "./contexts/ChatContext";
import { useChatService } from "./services/ChatService";

const NewWindowChat = (props = {}) => {
  const isPortalMode = props && props.messages !== undefined;

  // If running as a portal (rendered by NewWindowChatManager), use props-driven state
  const chat = isPortalMode
    ? {
        messages: props.messages || [],
        inputDraft: props.inputDraft || "",
        loading: props.loading || false,
        error: props.error || null,
        conversationID: props.conversationID || null,
      }
    : useChat();

  const chatService = isPortalMode
    ? {
        sendMessage: props.onSend || (async () => {}),
        handleSuggestionClick: props.onSuggestionClick || (async () => {}),
        copyMessage: props.onCopy || (() => {}),
        rerunMessage: props.onRerun || (async () => {}),
        updateInputDraft: props.updateInputDraft || (() => {}),
        isSendDisabled: () => props.loading || !(props.inputDraft || "").trim(),
        getLatestAIMessageId: () =>
          (props.messages || [])
            .slice()
            .reverse()
            .find((m) => m.sender === "ai")?.id,
      }
    : useChatService();

  // Local state for portal input to avoid re-render fights
  const [localInput, setLocalInput] = useState(props.inputDraft || "");

  // Refs
  const latestMessageRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize chat service with context values when in non-portal mode
  useEffect(() => {
    if (!isPortalMode && chat.conversationID) {
      chatService.initializeChat(chat.conversationID, chat.initialSuggestions, chat.fileIds);
    }
  }, [isPortalMode, chat.conversationID, chat.initialSuggestions, chat.fileIds, chatService]);

  // Scroll to latest message
  useEffect(() => {
    if (latestMessageRef.current) {
      latestMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [chat.messages]);

  // Keep localInput in sync when parent pushes updates, but avoid overwriting while typing
  useEffect(() => {
    if (isPortalMode) {
      setLocalInput(props.inputDraft || "");
    }
  }, [props.inputDraft, isPortalMode]);

  // Handle input
  const handleSend = async (questionText = chat.inputDraft) => {
    // If portal mode, use localInput content
    if (isPortalMode) {
      const text = (localInput || "").trim();
      if (!text) return;
      await chatService.sendMessage(text);
      // clear local input
      setLocalInput("");
      // notify parent of cleared draft
      props.updateInputDraft && props.updateInputDraft("");
      return;
    }

    await chatService.sendMessage(questionText);
  };

  const handleSuggestionClick = async (suggestion, conversationID) => {
    await chatService.handleSuggestionClick(suggestion, conversationID);
  };

  const handleCopyMessage = (text) => {
    chatService.copyMessage(text);
  };

  const handleRerunMessage = async (previousQuestion, conversationID) => {
    await chatService.rerunMessage(previousQuestion, conversationID);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    // In new window, closing means closing the window
    window.close();
  };

  // Get latest AI message ID for rerun button state
  const latestAIMessageId = isPortalMode
    ? chatService.getLatestAIMessageId()
    : chatService.getLatestAIMessageId();

  return (
    <div
      style={{
        height: "100vh",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Lexend",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #eee",
          background: "#f8fafc",
          userSelect: "none",
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "orange",
            marginRight: 8,
          }}
        />
        <div
          style={{
            fontWeight: 600,
            fontSize: 18,
            flex: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          lawyal.ai
        </div>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 12,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {chat.messages.map((msg, index) => (
          <React.Fragment key={msg.id}>
            <div
              ref={index === chat.messages.length - 1 ? latestMessageRef : null}
              className={`
                ${
                  msg.sender === "user"
                    ? "self-end bg-indigo-100"
                    : "self-start bg-gray-50"
                }
                rounded-lg p-3 mb-2 max-w-[85%] ${
                  msg.sender === "user" ? "whitespace-pre-line" : ""
                }
              `}
              style={{
                lineHeight: "1.5",
                fontSize: "13px",
                fontFamily: "Lexend, sans-serif",
              }}
            >
              {msg.isLoading ? (
                <div className="flex items-center space-x-1">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              ) : msg.sender === "ai" ? (
                <div
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                  className="prose prose-sm max-w-none ai-message"
                />
              ) : (
                msg.text
              )}
            </div>

            {/* Action buttons for AI messages */}
            {msg.sender === "ai" && !msg.isLoading && (
              <div className="flex gap-1 mb-3 ml-1">
                <Button
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={() => handleCopyMessage(msg.text)}
                  styles={{
                    root: {
                      height: "24px",
                      width: "24px",
                      padding: "0",
                      minWidth: "unset",
                    },
                  }}
                >
                  <Copy size={12} />
                </Button>

                <Button
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={() => {
                    const previousUserMsg = chat.messages
                      .slice(0, index)
                      .reverse()
                      .find((m) => m.sender === "user");
                    if (previousUserMsg) {
                      handleRerunMessage(
                        previousUserMsg.text,
                        msg.conversationID
                      );
                    }
                  }}
                  disabled={chat.loading || msg.id !== latestAIMessageId}
                  styles={{
                    root: {
                      height: "24px",
                      width: "24px",
                      padding: "0",
                      minWidth: "unset",
                    },
                  }}
                >
                  <Refresh size={12} />
                </Button>
              </div>
            )}

            {/* Document References */}
            {msg.sender === "ai" &&
              !msg.isLoading &&
              msg.documentReferences &&
              msg.documentReferences.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    marginBottom: 12,
                    padding: 10,
                    backgroundColor: "#f9fafb",
                    borderRadius: 6,
                    fontSize: "11px",
                    maxWidth: "85%",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 6,
                      color: "#6b7280",
                    }}
                  >
                    📄 Sources:
                  </div>
                  {msg.documentReferences.map((ref, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: 6,
                        marginBottom: 4,
                        borderLeft: "2px solid #3b82f6",
                        paddingLeft: 8,
                      }}
                    >
                      <div style={{ fontWeight: 500, color: "#374151" }}>
                        {ref.file_name || `Document ${ref.file_id}`}
                      </div>
                      {ref.excerpt && (
                        <div
                          style={{
                            color: "#6b7280",
                            marginTop: 3,
                            fontStyle: "italic",
                          }}
                        >
                          "{ref.excerpt}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            {/* Suggestions */}
            <div className="max-w-[85%]">
              {msg.sender === "ai" &&
                !msg.isLoading &&
                msg.suggestions &&
                msg.suggestions.length > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                    className="mb-6"
                  >
                    {msg.suggestions.map((suggestion, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="xs"
                        className="p-2 h-auto"
                        styles={{
                          label: {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            textAlign: "left",
                            fontSize: "12px",
                          },
                        }}
                        onClick={() =>
                          handleSuggestionClick(suggestion, msg.conversationID)
                        }
                        disabled={chat.loading}
                        title={suggestion}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                )}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Input */}
      <div
        style={{
          padding: 12,
          borderTop: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <TextareaAutosize
          ref={inputRef}
          className="no-resize"
          value={isPortalMode ? localInput : chat.inputDraft}
          onChange={(e) => {
            if (isPortalMode) {
              setLocalInput(e.target.value);
            } else {
              chatService.updateInputDraft(e.target.value);
            }
          }}
          onBlur={() => {
            if (isPortalMode)
              props.updateInputDraft && props.updateInputDraft(localInput);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Type your question..."
          minRows={2}
          maxRows={4}
          style={{
            flex: 1,
            borderRadius: 6,
            border: "1px solid #ddd",
            padding: 6,
            lineHeight: 1.3,
            fontSize: 12,
            fontFamily: "Lexend, sans-serif",
          }}
          disabled={isPortalMode ? props.loading : chat.loading}
        />
        <Button
          onClick={handleSend}
          disabled={chatService.isSendDisabled()}
          size="xs"
          styles={{
            root: {
              background: "#61dafb",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "6px 12px",
              fontWeight: 600,
              fontSize: "12px",
              "&[data-disabled]": {
                background: "#96def1ff",
                opacity: 1,
                color: "#fff",
                cursor: "not-allowed",
              },
            },
          }}
        >
          Send
        </Button>
      </div>

      {chat.error && (
        <div
          style={{
            color: "red",
            textAlign: "center",
            padding: 8,
            fontSize: 12,
          }}
        >
          {chat.error}
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .typing-indicator span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #6b7280;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) {
          animation-delay: 0s;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%,
          60%,
          100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }

        .prose {
          max-width: none;
          color: #1f2937;
          line-height: 1.6;
          font-size: 13px;
          font-family: "Lexend", sans-serif;
        }

        .prose p {
          margin: 0.5em 0;
        }

        .prose strong {
          font-weight: 600;
          color: #111827;
        }

        .prose ul,
        .prose ol {
          list-style: disc outside !important;
          padding-left: 1.2em !important;
          margin: 0.5em 0 !important;
        }

        .prose li {
          margin-bottom: 0.3em !important;
          line-height: 1.5;
        }

        .no-resize {
          resize: none !important;
          -webkit-resize: none !important;
        }

        .self-end {
          align-self: flex-end;
        }

        .self-start {
          align-self: flex-start;
        }

        .bg-indigo-100 {
          background-color: #e0e7ff;
        }

        .bg-gray-50 {
          background-color: #f9fafb;
        }

        .rounded-lg {
          border-radius: 8px;
        }

        .p-3 {
          padding: 12px;
        }

        .mb-2 {
          margin-bottom: 8px;
        }

        .mb-3 {
          margin-bottom: 12px;
        }

        .mb-6 {
          margin-bottom: 24px;
        }

        .ml-1 {
          margin-left: 4px;
        }

        .max-w-[85%] {
          max-width: 85%;
        }

        .whitespace-pre-line {
          white-space: pre-line;
        }

        .flex {
          display: flex;
        }

        .items-center {
          align-items: center;
        }

        .space-x-1 > * + * {
          margin-left: 4px;
        }

        .gap-1 {
          gap: 4px;
        }

        .gap-4 {
          gap: 16px;
        }

        .flex-direction-column {
          flex-direction: column;
        }

        .h-auto {
          height: auto;
        }

        .p-2 {
          padding: 8px;
        }
      `}</style>
    </div>
  );
};

export default NewWindowChat;
